import React, { useState, useEffect, useCallback } from "react";
import { updateTitle } from "../lib/utils";
import { loadClientListConditionally } from "../store";
import driverconnect from "../driverconnect.svg";
import { Input, Button, Loading, Row, Modal, Text } from '@nextui-org/react';
import {
  loginAndSaveCreds,
  updateBasicUserInfo,
  userPasswordReset,
} from "../auth0";
import LogoImage from "./../assets/logo-final.jpg";

const PRIMARY_COLOR = "#6366f1"; // Indigo-500

const Login = (props) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [forgotPasswordDialog, setForgotPasswordDialog] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    updateTitle("Login");
    if (window.innerWidth <= 699) {
      setMobile(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "email") setEmail(value);
    else if (name === "password") setPassword(value);
    else if (name === "remember") setRemember(type === "checkbox" ? checked : value);
  };

  const forgotPassword = useCallback(() => {
    if (email.length < 3) {
      setIsError(true);
      setMessage("Please enter your email to continue!");
    } else {
      userPasswordReset(email).then((msg) => {
        setIsError(false);
        setMessage(msg);
      });
    }
  }, [email]);

  const navigateToSignup = () => {
    props.history.push("/signup");
  };

  const submitLoginForm = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!email || !password) return;

      setIsChecking(true);
      setIsError(false);
      setMessage("");

      loginAndSaveCreds(email, password, remember)
        .then(() => {
          updateBasicUserInfo()
            .then((userObj) => {
              if (userObj.isFreeUser !== 1) {
                if (userObj.companyIsActive === 1) {
                  loadClientListConditionally(userObj);
                  props.history.replace("/products");
                } else {
                  setIsChecking(false);
                  setIsError(true);
                  setMessage("Company is not active");
                }
              } else {
                setIsChecking(false);
                setIsError(true);
                setMessage("You are not authorized to access this portal");
              }
            })
            .catch((err) => {
              console.error(err);
              setIsChecking(false);
              setIsError(true);
              setMessage("Cannot fetch user information from server!");
            });
        })
        .catch((err) => {
          setIsChecking(false);
          setIsError(true);
          setMessage(err.description || "Oops! Something wrong has happned");
        });
    },
    [email, password, remember, props.history]
  );

  const activateForgotDialog = () => setForgotPasswordDialog(true);

  const onKeyPress = (e) => {
    if (e.which === 13) {
      submitLoginForm(e);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center"
      style={{
        background: "linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)",
      }}
    >
      <div className="flex w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Left: Big Logo */}
        <div className="flex flex-col justify-center items-center p-8" style={{ flex: 1 }}>
          <img
            alt="login logo"
            src={LogoImage}
            style={{
              width: 320,
              height: 320,
              borderRadius: 24
            }}
          />
        </div>
        {/* Right: Login Card */}
        <div className="flex-1 p-8 flex flex-col justify-center">
          <div className="flex flex-col items-center mb-6">
            <h2 className="text-2xl font-bold mb-2" style={{ color: PRIMARY_COLOR }}>
              Welcome Back
            </h2>
            <p className="text-gray-500 mb-2">Sign in to your account</p>
          </div>
          <form onSubmit={submitLoginForm}>
            <Input
              name="email"
              size="lg"
              color="primary"
              placeholder="Email (User Name)"
              onChange={handleChange}
              value={email}
              fullWidth
              bordered
              css={{
                mb: 16,
                borderRadius: 8,
                borderColor: PRIMARY_COLOR,
                '&:focus-within': { borderColor: PRIMARY_COLOR }
              }}
            />

            <Input.Password
              name="password"
              size="lg"
              color="primary"
              placeholder="Enter your password"
              onChange={handleChange}
              onKeyPress={onKeyPress}
              value={password}
              fullWidth
              bordered
              css={{
                mb: 16,
                borderRadius: 8,
                borderColor: PRIMARY_COLOR,
                '&:focus-within': { borderColor: PRIMARY_COLOR }
              }}
            />

            {isError && (
              <div className="text-red-600 text-md font-semibold mb-2">{message}</div>
            )}

            <Button
              type="submit"
              auto
              size="lg"
              css={{
                width: "100%",
                background: PRIMARY_COLOR,
                color: "#fff",
                fontWeight: 600,
                borderRadius: 8,
                mb: 12,
                boxShadow: "0 2px 8px #e0e7ff",
                '&:hover': { background: "#4f46e5" }
              }}
              disabled={isChecking}
            >
              {isChecking ? (
                <Loading type="points" color="white" size="md" />
              ) : (
                <span>Login</span>
              )}
            </Button>

            <Row justify="space-between" className="mb-4">
              <a
                className="cursor-pointer text-sm"
                style={{ color: PRIMARY_COLOR, fontWeight: 500 }}
                onClick={activateForgotDialog}
              >
                Forgot Password?
              </a>
              <a
                className="cursor-pointer text-sm"
                style={{ color: PRIMARY_COLOR, fontWeight: 500 }}
                onClick={navigateToSignup}
              >
                Register
              </a>
            </Row>
          </form>
        </div>
      </div>

      <div className="mt-8 text-gray-400 text-sm text-center">
        Driver focused and value driven &middot; &copy; {new Date().getFullYear()}
      </div>

      {/* Forgot Password Modal */}
      <Modal
        open={forgotPasswordDialog}
        onClose={() => {
          setIsError(false);
          setMessage("");
          setForgotPasswordDialog(false);
        }}
        closeButton
        blur
      >
        <Modal.Header>
          <Text size={24}>
            Forgot Password
          </Text>
        </Modal.Header>

        <Modal.Body>
          <Input
            disabled={false}
            underlined
            clearable
            placeholder="Email Address"
            type="email"
            labelLeft="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required={true}
            className="w-100 mv1"
          />
          <Button
            auto
            css={{ br: 0 }}
            className="mt-3"
            onClick={forgotPassword}
            disabled={isChecking}
          >
            {isChecking ? (
              <Loading type="points" color="white" size="md" />
            ) : (
              <span>Send password reset email</span>
            )}
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <label
            className={
              "f5 w-100 pa2 " +
              (isError || message ? "" : "invisible")
            }
          >
            <div
              style={{
                display: "inline",
                marginLeft: "5px",
                color: isError ? "red" : "black"
              }}
            >
              {message}
            </div>
          </label>
        </Modal.Footer>
      </Modal>

      {/* Mobile Modal */}
      <Modal
        open={mobile}
        className="cmtenghdr mh2"
        title="Login Confirmation"
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
      >
        <div className="mt3 h-25 mh4">
          <div>
            <div style={{ marginBottom: "5px", fontSize: "15px" }}>
              Continue using Driver Connect Portal
            </div>
            <Button
              className="w4 mr3"
              onClick={() => setMobile(false)}
            >
              Click here
            </Button>
          </div>
          <div>
            <img
              style={{ width: "50px", marginTop: "20px" }}
              src={driverconnect}
              alt={'Driver Connect'}
              className="w2"
            />
            <div
              style={{
                marginBottom: "5px",
                fontSize: "15px",
                marginTop: "2px",
              }}
            >
              To download SDN DriverConnect App
            </div>
            <Button
              className="w4 mr3"
              onClick={() => {
                window.location = "http://onelink.to/gb6x3q";
              }}
            >
              Click here
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Login;